import { type ApprovalPoolConstructorData } from '@webapp/core/entities/internal/ApprovalPool'
import { type ApprovalWorkflowConstructorData } from '@webapp/core/entities/internal/ApprovalWorkflow'
import { type ApproverConstructor } from '@webapp/core/entities/internal/Approver'
import { type OrderSummaryConstructor } from '@webapp/modules/purchasing/entities/OrderSummary'
import {
  type OrderSummaryApproverApiModel,
  type OrderSummaryNextApproverOptionApiModel,
  type OrderSummaryApiModel,
} from '../api-models/OrderSummaryApiModel'

const mapOrderSummaryApprover = (
  approver: OrderSummaryApproverApiModel
): ApproverConstructor => ({
  id: approver.id,
  firstName: approver.firstName,
  lastName: approver.lastName,
  profileImage: approver.profile_image ?? null,
  approvalDelegatee: approver.approval_delegatee
    ? {
        id: approver.approval_delegatee.id,
        firstName: approver.approval_delegatee.firstName,
        lastName: approver.approval_delegatee.lastName,
        expectedReturnDate:
          approver.approval_delegatee.expected_return_date ?? null,
      }
    : null,
  primaryCategory: null,
  secondaryCategory: null,
})

// Reshapes {group_id, group_name, is_pooled, level, approvers} options into
// ApprovalWorkflowConstructorData.nextStep.approverChoices. Each wire option
// collapses into exactly ONE picker choice, keyed off the group's approver
// count rather than `is_pooled` — the same rule NextApproverSelectBridge and
// mapOrderApiModel apply, so a lone option stays a single auto-selectable
// choice and approving a multi-approver group routes to every member.
const mapNextApproverOptions = (
  options: OrderSummaryNextApproverOptionApiModel[]
): (ApproverConstructor | ApprovalPoolConstructorData)[] =>
  options.reduce<(ApproverConstructor | ApprovalPoolConstructorData)[]>(
    (acc, option) => acc.concat(mapNextApproverOption(option)),
    []
  )

const mapNextApproverOption = (
  option: OrderSummaryNextApproverOptionApiModel
): (ApproverConstructor | ApprovalPoolConstructorData)[] => {
  if (option.approvers.length === 0) {
    return []
  }

  if (option.approvers.length > 1) {
    const pool: ApprovalPoolConstructorData = {
      level: option.level,
      groupName: option.group_name,
      groupId: option.group_id,
      members: option.approvers.map(mapOrderSummaryApprover),
    }
    return [pool]
  }

  return [
    {
      ...mapOrderSummaryApprover(option.approvers[0]),
      level: option.level,
      groupName: option.group_name,
    },
  ]
}

export const mapOrderSummaryApiModel = (
  raw: OrderSummaryApiModel
): OrderSummaryConstructor => {
  // Mandatory delegation-state correction (StatusListManagerFormat.js:76-80
  // parity): a null row-level approval_delegatee means there is no live
  // delegation, so the stale nested next_approver.approval_delegatee must be
  // nulled out.
  const mappedNextApprover = raw.next_approver
    ? mapOrderSummaryApprover(raw.next_approver)
    : null

  const nextApprover: Nullable<ApproverConstructor> = mappedNextApprover
    ? {
        ...mappedNextApprover,
        approvalDelegatee: raw.approval_delegatee
          ? mappedNextApprover.approvalDelegatee
          : null,
      }
    : null

  const approvalWorkflow: Nullable<ApprovalWorkflowConstructorData> = raw
    .approval_workflow?.next_step
    ? {
        currentStep: null,
        nextStep: {
          groupId: raw.approval_workflow.next_step.group_id,
          groupName: raw.approval_workflow.next_step.group_name,
          approverChoices: mapNextApproverOptions(
            raw.approval_workflow.next_step.options
          ),
        },
      }
    : null

  const punchoutShippingAddressName: Nullable<string> =
    typeof raw.punchout_shipping_address === 'string'
      ? raw.punchout_shipping_address
      : (raw.punchout_shipping_address?.name ?? null)

  return {
    orderNumber: raw.order_number ?? raw.num,
    num: raw.num,
    uuid: raw.uuid,
    status: raw.status,
    description: raw.description,
    totalCostInBaseCurrency: raw.total_cost_in_base_currency,
    itemCount: raw.item_count,
    submittedDate: raw.date,
    dateRequired: raw.dateRequired,
    primaryCategoryName: raw.department.branch.name,
    secondaryCategoryName: raw.department.name,
    nextApprover,
    nextApproverChoices: raw.next_approver_choices
      ? raw.next_approver_choices.map(mapOrderSummaryApprover)
      : null,
    approvalWorkflow,
    hasPendingItems: raw.has_pending_items,
    hasPendingPunchoutItems: raw.has_pending_punchout_items,
    hasBlanketOrderItems: raw.has_blanket_order_items,
    delegatedBy: raw.delegated_by,
    isPunchoutOrder: raw.punchout_group !== null,
    punchoutItemsRejected: raw.punchout_items_rejected,
    punchoutSupplierName: raw.punchout_supplier_name,
    punchoutSupplierIcon: raw.punchout_supplier_icon,
    punchoutShippingAddressName,
  }
}
